import React from "react";
import type { ReactElement } from "react";
import { Skeleton } from "@ndd/ui";
import type { CompactRdMetrics } from "../../../../shared/metrics";
import type { FunctionalFormLink } from "../../lib/metrics-display";
import {
  HEADLINE_METRICS,
  formatDataThrough,
  sourceLabel,
} from "../../lib/snapshot-display";
import {
  MetricTile,
  TILE_CLASS,
  TILE_HEADER_CLASS,
  TILE_SENTENCE_CLASS,
  TILE_VALUE_ROW_CLASS,
} from "./metric-tile";

const GRID_CLASS = "grid grid-cols-1 gap-4 md:grid-cols-2";

/** Same fixed tracks as MetricTile so the loading grid keeps the tile alignment. */
function TileSkeleton(): ReactElement {
  return (
    <div className={TILE_CLASS} aria-hidden="true" data-testid="metric-tile-skeleton">
      <div className={TILE_HEADER_CLASS}>
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      <div className={TILE_VALUE_ROW_CLASS}>
        <Skeleton className="h-8 w-24" />
      </div>
      <div className={TILE_SENTENCE_CLASS}>
        <Skeleton className="mb-2 h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
      <div className="pt-1">
        <Skeleton className="h-[6px] w-full rounded-full" />
      </div>
      <div className="flex flex-col gap-1">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    </div>
  );
}

/** Loading footprint: six tiles keep their shape while value, sentence, and bar load. */
export function HeadlineStripSkeleton(): ReactElement {
  return (
    <div className={GRID_CLASS} data-testid="headline-strip-loading">
      {HEADLINE_METRICS.map((config) => (
        <TileSkeleton key={config.name} />
      ))}
    </div>
  );
}

/** The 2x3 strip of headline metric tiles plus its data-through footer. */
export function HeadlineStrip({
  metrics,
  functionalFormLink,
}: {
  metrics: CompactRdMetrics;
  /** Wraps the missing-minimum flag's link; the route supplies it. */
  functionalFormLink?: FunctionalFormLink;
}): ReactElement {
  const sources = [
    ...new Set(HEADLINE_METRICS.map((config) => metrics.metrics[config.name].source)),
  ];

  return (
    <div className="flex flex-col gap-2" data-testid="headline-strip">
      <div className={GRID_CLASS}>
        {HEADLINE_METRICS.map((config) => (
          <MetricTile
            key={config.name}
            config={config}
            metric={metrics.metrics[config.name]}
            functionalFormLink={functionalFormLink}
          />
        ))}
      </div>
      <p className="px-1 text-xs text-muted-foreground">
        Values through {formatDataThrough(metrics.dataThrough)} ·{" "}
        {sources.map(sourceLabel).join(" · ")}
      </p>
    </div>
  );
}
